import gsap from 'gsap';

function init( node ) {
	if ( ! ( node instanceof HTMLElement ) ) {
		return;
	}

	// Nudge the background video to play. Declarative autoplay is unreliable, and some
	// browsers/platforms (battery saver, denied autoplay permission) block muted autoplay
	// outright — in that case fall back to playing on the first user interaction.
	const media = node.querySelector( '.hero__media' );
	if ( media instanceof HTMLVideoElement ) {
		media.muted = true;

		const tryPlay = (e) => {
			console.log( e ?? null );
			media.play().catch( () => {} )
		};
		const gestures = [ 'pointerdown', 'keydown', 'touchstart' ];

		gestures.forEach( ( type ) => document.addEventListener( type, tryPlay, { passive: true } ) );
		media.addEventListener(
			'playing',
			() => gestures.forEach( ( type ) => document.removeEventListener( type, tryPlay ) ),
			{ once: true }
		);

		tryPlay();
	}

	// Reveal the optional content section as it scrolls into view
	const content = node.querySelector( '.hero__content-layout' );
	if ( content && 'IntersectionObserver' in window ) {
		content.classList.add( 'hero__content-layout--reveal' );

		const observer = new IntersectionObserver(
			( entries, instance ) => {
				entries.forEach( ( entry ) => {
					if ( ! entry.isIntersecting ) {
						return;
					}

					entry.target.classList.add( 'is-revealed' );
					instance.unobserve( entry.target );
				} );
			},
			{ threshold: 0.2 }
		);

		observer.observe( content );
	}

	// Pin the background video to the viewport but keep it cropped to the taglines
	// section — like background-attachment: fixed. Skipped when motion is reduced.
	const taglinesSection = node.querySelector( '.hero__taglines' );
	const prefersReducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

	if ( media instanceof HTMLVideoElement && taglinesSection && ! prefersReducedMotion ) {
		node.classList.add( 'hero--media-fixed' );

		const overlay = node.querySelector( '.hero__overlay' );
		const layers = [ media, overlay ].filter( Boolean );

		// A fixed element's height resolves against the viewport, so pin it to the
		// taglines section. The clip-path on .hero crops it as the section scrolls.
		const syncHeight = () => {
			const height = `${ taglinesSection.getBoundingClientRect().height }px`;

			layers.forEach( ( layer ) => {
				layer.style.height = height;
			} );
		};

		syncHeight();
		window.addEventListener( 'resize', syncHeight );
	}

	const rotator = node.querySelector( '.tagline-rotator' );
	const lines = node.querySelector( '.tagline-rotator__lines' );

	const taglines = Array.from( node.querySelectorAll( '.tagline-rotator__line' ) );

	if ( ! rotator || ! taglines.length ) {
		return;
	}

	// Block options passed through from the render callback (esc_attr renders true as "1", false as "")
	const loop = !! node.dataset.loop;
	const revealMasthead = !! node.dataset.revealMasthead;

	// When looping, every tagline cycles; otherwise the last one is the resting phrase
	const finalTagline = taglines[ taglines.length - 1 ];
	const rotatingTaglines = loop ? taglines : taglines.slice( 0, -1 );

	// The masthead is faded in once the first animation pass finishes
	const masthead = revealMasthead ? document.querySelector( '.masthead' ) : null;

	// Timeline
	const tl = gsap.timeline( { paused: true } );

	// Set initial states
	gsap.set( taglines, { autoAlpha: 0, y: 0 } );
	gsap.set( lines, {
		alignSelf: 'center',
		display: 'flex',
		alignItems: 'center',
	} );

	// Hide rotator initially until the reveal animation runs
	gsap.set( rotator, { autoAlpha: 0 } );

	if ( masthead ) {
		gsap.set( masthead, { autoAlpha: 0 } );
	}

	// Constants for animation
	const animation = {
		duration: {
			initial: 0.25,
			standard: 0.4,
			pause: 1.2,
		},
		distance: 30,
		ease: {
			in: 'power3.in',
			out: 'power3.out',
			initial: 'power2.out',
		},
	};

	const showMasthead = () => {
		gsap.to( masthead, {
			autoAlpha: 1,
			duration: animation.duration.standard,
			ease: animation.ease.out,
		} );
	};

	// Reveal the rotator
	tl.to( rotator, {
		autoAlpha: 1,
		y: 0,
		duration: animation.duration.initial,
		ease: animation.ease.initial,
	} ).set( lines, { visibility: 'visible' } );

	// Animate the rotating taglines, each held for its own duration
	rotatingTaglines.forEach( ( tagline, index ) => {
		const position = index === 0 ? '>+=0.4' : '+=0.1';
		const hold = parseFloat( tagline.dataset.duration ) || animation.duration.pause;

		// Animate tagline in
		tl.fromTo(
			tagline,
			{ autoAlpha: 0, y: animation.distance },
			{
				autoAlpha: 1,
				y: 0,
				duration: animation.duration.standard,
				ease: animation.ease.out,
			},
			position
		);

		// Hold for this tagline's duration
		tl.to( {}, { duration: hold } );

		// Animate tagline out
		tl.to( tagline, {
			autoAlpha: 0,
			y: -animation.distance,
			duration: animation.duration.standard,
			ease: animation.ease.in,
		} );
	} );

	// When not looping, the final tagline animates in and rests there
	if ( ! loop ) {
		tl.fromTo(
			finalTagline,
			{ autoAlpha: 0, y: animation.distance },
			{
				autoAlpha: 1,
				y: 0,
				duration: animation.duration.standard,
				ease: animation.ease.out,
			},
			'+=0.1'
		);
	}

	// Fade the masthead in at the end of the first animation pass
	if ( masthead ) {
		if ( loop ) {
			let mastheadShown = false;

			tl.eventCallback( 'onRepeat', () => {
				if ( mastheadShown ) {
					return;
				}

				mastheadShown = true;
				showMasthead();
			} );
		} else {
			tl.eventCallback( 'onComplete', showMasthead );
		}
	}

	if ( loop ) {
		tl.repeat( -1 );
	}

	// Start the text timeline in sync with the video's first frame of playback so the
	// taglines line up with the video's transitions. Fall back to a timer so the hero
	// never stays blank if the video can't autoplay (blocked / no interaction / no video).
	const timelineStartFallback = 2000;
	let started = false;

	const startTimeline = () => {
		if ( started ) {
			return;
		}

		started = true;
		tl.play();
	};

	if ( media instanceof HTMLVideoElement ) {
		media.addEventListener( 'playing', startTimeline, { once: true } );
		setTimeout( startTimeline, timelineStartFallback );
	} else {
		startTimeline();
	}
}

// Initialize
const heroElement = document.querySelector( '.hero' );
if ( heroElement ) {
	init( heroElement );
}
